import { useCallback, useState } from "react";
import { useParams } from "next/navigation";
import Link from "next/link";
import { useQueryClient, useQuery } from "@tanstack/react-query";
import { Alert, Spinner } from "react-bootstrap";

import {
  AttributesView,
  BackLink,
  Card,
  CardGrid,
  CopyValue,
  HeaderDetail,
  Inspector,
  Page,
  toAttributes,
} from "@bagel-web/components";

import {
  AgentConfig,
  CommonAgentDetails,
  CommonSessionDetails,
  SessionDetails,
  util,
} from "@bagel-web/common";

import { ScrollingTextArea } from "../session/sidebar/MomentDetailPlayground";
import { agentQueries } from "../queries";

const AgentDetailPage = ({
  fetchAgentDetails,
  fetchSession,
  showApiRequestsLink = false,
  showMomentsLink = false,
}: {
  fetchAgentDetails: (
    sessionId: string,
    agentId: string
  ) => Promise<CommonAgentDetails>;
  fetchSession: (sessionId: string) => Promise<CommonSessionDetails>;
  showApiRequestsLink?: boolean;
  showMomentsLink?: boolean;
}) => {
  const { sessionId, agentId } = useParams<{
    sessionId: string;
    agentId: string;
  }>();

  const {
    isPending,
    isError,
    data: agent,
    error,
  } = useQuery({
    queryKey: agentQueries.get(sessionId, agentId),
    queryFn: () => fetchAgentDetails(sessionId, agentId),
  });

  const [session, setSession] = useState<CommonSessionDetails | null>(null);
  const queryClient = useQueryClient();

  const handlePreviewSession = useCallback(async () => {
    if (session) {
      return;
    }
    const sessionData = await fetchSession(sessionId);
    setSession(sessionData);
  }, [queryClient, setSession, session]);

  const agentConfig: AgentConfig | undefined = agent?.config;

  return (
    <Page>
      <Page.Header>
        <BackLink href={`/sessions/${sessionId}`} LinkComponent={Link}>
          Session
        </BackLink>
        <h2>Agent</h2>
        <HeaderDetail>
          <HeaderDetail.Field label="Session">
            <Inspector
              View={SessionDetails}
              viewProps={{ session: session || null }}
              onEnter={handlePreviewSession}
              showLoading={!session}
            >
              <CopyValue>{sessionId}</CopyValue>
            </Inspector>
          </HeaderDetail.Field>

          <HeaderDetail.Field label="Agent">
            <CopyValue>{agentId}</CopyValue>
          </HeaderDetail.Field>

          {agentConfig && (
            <>
              <HeaderDetail.Field label="Creation time">
                {util.formatTimestamp(agentConfig.created)}
              </HeaderDetail.Field>

              <HeaderDetail.Field label="Destroyed time">
                {util.formatTimestamp(agentConfig.destroyed)}
              </HeaderDetail.Field>

              <HeaderDetail.Field label="ASH">
                <code>{agentConfig.name}</code>
              </HeaderDetail.Field>

              <HeaderDetail.Field label="LLM">
                <code>{agentConfig?.config?.agent_llm}</code>
              </HeaderDetail.Field>

              <HeaderDetail.Field label="Temperature">
                <code>
                  {agentConfig?.config?.llm_config?.config?.temperature}
                </code>
              </HeaderDetail.Field>

              <HeaderDetail.Field label="Metadata">
                <AttributesView attributes={toAttributes(agent?.metadata)} />
              </HeaderDetail.Field>
            </>
          )}
        </HeaderDetail>
        {showApiRequestsLink && (
          <div>
            <Link
              href={`/sessions/${sessionId}/api-requests?agentId=${agentId}`}
              className="card-link"
            >
              API requests
            </Link>
          </div>
        )}

        {showMomentsLink && (
          <div>
            <Link
              href={`/sessions/${sessionId}/moments?agentId=${agentId}`}
              className="card-link"
            >
              Moments
            </Link>
          </div>
        )}

        <div>
          <Link
            href={`/sessions/${sessionId}/agent/${agentId}/event-stream`}
            className="card-link"
          >
            Message stream
          </Link>
        </div>
      </Page.Header>
      <Page.Body>
        {isPending && !isError && <Spinner />}
        {!isPending && !isError && agent && (
          <>
            <h3>Role</h3>
            <CardGrid unitWidth={800}>
              <Card title="Core" id="role-core">
                <ScrollingTextArea style={{ whiteSpace: "pre-wrap" }}>
                  {agent.role_config?.core}
                </ScrollingTextArea>
              </Card>
              <Card title="Characterization" id="role-characterization">
                <ScrollingTextArea style={{ whiteSpace: "pre-wrap" }}>
                  {agent.role_config?.characterization}
                </ScrollingTextArea>
              </Card>
            </CardGrid>
          </>
        )}
        {isError && (
          <Alert variant="warning">
            Error loading agent details:<div>{error.message}</div>
          </Alert>
        )}
      </Page.Body>
    </Page>
  );
};

export default AgentDetailPage;
